/**
 * initialsAvatar
 *
 * Generates a deterministic initials avatar as an inline SVG data URI. Used as
 * the fallback image for buyers, students, producers and products that have no
 * picture.
 *
 * The look matches the course dashboard "Alunos recentes" letter-avatars: a pale
 * tint background with the initials in the full hue, picked deterministically
 * from the name. Because the result is a `data:` URI it is rendered as-is: no
 * external request and no extra dependency.
 *
 * History:
 *   05-10-2026: Created to replace the remote avatars (ported from marketplace).
 *   06-10-2026: Restyled to the light tint + hue initials look, converged with the course dashboard.
 */

// The course dashboard's 6 sp-color hues (manage-v2/dashboard), so a student
// keeps the same colour in the Alunos list and in "Alunos recentes".
const HUES = ['#1890ff', '#fa8c16', '#722ed1', '#52c41a', '#13c2c2', '#eb2f96'];

// Same hash the dashboard uses, keyed on the name, so colours match per student.
// Deterministic: the same name always maps to the same hue.
const hashString = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

// Up to two initials, from letter runs only. Non-letters (digits, "-", "#",
// other punctuation) act as separators and never appear in the result, so
// "check-vat-5" yields "CV" and "Ana Paula Queiroz" yields "AQ". A single word
// uses its first two letters ("Fotografia" -> "FO"). Falls back to "?".
const getInitials = (name) => {
  const tokens = String(name || '').match(/\p{L}+/gu) || [];
  if (tokens.length === 0) return '?';
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return (tokens[0][0] + tokens[tokens.length - 1][0]).toUpperCase();
};

const escapeXml = (str) =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// A hue mixed `amount` into white, returned as a `#rrggbb` hex. color-mix() is
// unreliable inside a data: URI SVG, so the tint is computed here. 0.15 is the
// dashboard's 15% (avatarStyle: color-mix(in srgb, hue 15%, #ffffff)).
const tint = (hex, amount = 0.15) => {
  const n = parseInt(hex.slice(1), 16);
  const mix = (ch) => Math.round(ch * amount + 255 * (1 - amount));
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
};

/**
 * Build the avatar data URI for a given name/title.
 *
 * @param {string} name Person name or product title.
 * @returns {string} A `data:image/svg+xml,...` URI, or an empty string on failure.
 */
export default function initialsAvatar(name) {
  try {
    const initials = getInitials(name);
    const hue = HUES[hashString(String(name || '')) % HUES.length];
    const bg = tint(hue);

    const svg =
      '<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 400 400">' +
      `<rect width="400" height="400" fill="${bg}"/>` +
      '<text x="50%" y="50%" dy=".35em" text-anchor="middle" ' +
      'font-family="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" ' +
      `font-size="170" font-weight="700" fill="${hue}">${escapeXml(initials)}</text>` +
      '</svg>';

    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
  } catch (error) {
    return '';
  }
}
